import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, StatCard, Panel, Pill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Sparkle, ChatCircleText, ChartLine, Heart,
  Lifebuoy, Plus, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_TONES = {
  open: "warning",
  in_progress: "info",
  waiting_on_user: "muted",
  resolved: "success",
  closed: "muted",
  escalated: "danger",
};

export default function TechCoordDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/support-tickets/stats"),
          api.get("/support-tickets", { params: { limit: 8 } }),
        ]);
        if (!cancelled) {
          setStats(s.data);
          setRecent(r.data.items || []);
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="tech-coord-dashboard">
      <PageHeader
        eyebrow="Technical Coordinator"
        title={`Welcome, ${user?.name?.split(" ")[0] || "Tech"}!`}
        subtitle="Keep MangoCubs running smoothly — track tickets, parent app issues, QR scanner setup, and device health."
        action={
          <Link to="/app/tech-support">
            <Button className="bg-terracotta hover:bg-terracotta/90 text-white" data-testid="open-tickets-btn">
              <Lifebuoy size={16} /> Open ticket queue
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Open"
          value={stats?.by_status?.open ?? 0}
          icon={ShieldCheck}
          accent="terracotta"
          testid="kpi-open"
        />
        <StatCard
          label="In progress"
          value={stats?.by_status?.in_progress ?? 0}
          icon={ChartLine}
          accent="mustard"
          testid="kpi-in-progress"
        />
        <StatCard
          label="Resolved"
          value={stats?.by_status?.resolved ?? 0}
          icon={Heart}
          accent="sage"
          testid="kpi-resolved"
        />
        <StatCard
          label="Escalated"
          value={stats?.by_status?.escalated ?? 0}
          icon={Sparkle}
          accent="lavender"
          testid="kpi-escalated"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Panel title="Recent tickets" className="lg:col-span-2">
          {loading ? (
            <div className="py-12 text-center text-ink-soft text-sm">Loading…</div>
          ) : recent.length === 0 ? (
            <div className="py-12 text-center text-ink-soft text-sm">
              No tickets yet. Once parents or staff report issues they'll appear here.
            </div>
          ) : (
            <div className="space-y-2" data-testid="recent-tickets-list">
              {recent.map((t) => (
                <Link
                  key={t.ticket_id}
                  to={`/app/tech-support`}
                  className="flex items-center justify-between p-3 rounded-lg bg-paper hover:bg-paper-alt border border-border transition"
                  data-testid={`ticket-row-${t.ticket_id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    <div className="text-xs text-ink-soft truncate">
                      #{t.ticket_id?.slice(-6)} · {t.category} ·{" "}
                      {t.severity?.toUpperCase()}
                    </div>
                  </div>
                  <Pill tone={STATUS_TONES[t.status] || "muted"}>{t.status}</Pill>
                </Link>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Quick actions" className="lg:col-span-1">
          <div className="space-y-2">
            <Link
              to="/app/tech-support"
              className="block p-3 rounded-xl bg-terracotta-soft hover:bg-terracotta-soft/80 transition"
              data-testid="action-create-ticket"
            >
              <div className="flex items-center gap-2 text-terracotta font-semibold text-sm">
                <Plus size={16} /> New ticket
              </div>
              <div className="text-xs text-ink-soft mt-1">
                Log a new tech support issue.
              </div>
            </Link>
            <Link
              to="/app/users"
              className="block p-3 rounded-xl bg-sage-soft hover:bg-sage-soft/80 transition"
              data-testid="action-user-lookup"
            >
              <div className="flex items-center gap-2 text-sage-deep font-semibold text-sm">
                <ChatCircleText size={16} /> User lookup
              </div>
              <div className="text-xs text-ink-soft mt-1">
                Help with parent / staff login support.
              </div>
            </Link>
            <Link
              to="/app/serial-and-qr"
              className="block p-3 rounded-xl bg-mustard-soft hover:bg-mustard-soft/80 transition"
              data-testid="action-qr-scanner"
            >
              <div className="flex items-center gap-2 text-mustard-hover font-semibold text-sm">
                <ShieldCheck size={16} /> QR scanner testing
              </div>
              <div className="text-xs text-ink-soft mt-1">
                Verify gate pass + ID scanning works.
              </div>
            </Link>
            <a
              href="mailto:support@mangocubs.com"
              className="block p-3 rounded-xl bg-paper-alt hover:bg-border/20 transition"
              data-testid="action-escalate"
            >
              <div className="flex items-center gap-2 text-ink font-semibold text-sm">
                <ArrowSquareOut size={16} /> Escalate to MangoCubs
              </div>
              <div className="text-xs text-ink-soft mt-1">
                Email central support team.
              </div>
            </a>
          </div>
        </Panel>
      </div>

      <Panel title="Tech health checklist">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
          {[
            ["Camera permissions for QR scanner", "Test from a teacher device weekly"],
            ["LMS content loading", "Verify after each release"],
            ["Photo / video upload pipeline", "Verify largest tested file ≥ 50 MB"],
            ["Smart board / digi-board", "Check HDMI + browser sync"],
            ["Internet failover", "Confirm 4G hotspot is paid up"],
            ["Parent app push", "Send a test push to a staff phone"],
          ].map(([title, hint]) => (
            <div
              key={title}
              className="p-3 rounded-xl border border-border bg-paper"
              data-testid={`health-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="font-semibold text-sm">{title}</div>
              <div className="text-xs text-ink-soft mt-1">{hint}</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
